import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Box, Typography } from '@mui/material';

import { ErrorWrapper } from 'shared-components/ErrorWrapper';
import { getRandomIndex } from 'utils/random';
import { FormWrapper } from 'components/common/FormWrapper';
import {
  FormDescription,
  FormRow,
  FormTitle,
} from 'components/common/FormWrapper/styles';
import { PKDFilledButton } from 'components/FilledButton';
import { PKDOnboardingLayout } from 'components/PKDOnboardingLayout';
import { PKDSelect } from 'components/PKDSelect';
import MedicalInfoImage from 'images/medical-info.png';
import { getDaysOfMonth, isBelowAge, getArrayOfYears, formatDate } from 'utils/date';
import { updateSecondaryOverviewStep } from 'store/overview';
import {
  PKDDiagnosis,
  PersonStatus,
  Relationship,
  StudyRoleId,
} from 'utils/enum';
import { useGetPersonAndAttributes } from 'hooks/useGetPersonAndAttributes';
import {
  useUpsertPersonAttributeMutation,
  useGetPersonStudyRolesQuery,
  useCreatePersonStudyRoleMutation,
} from 'store/api/pkd/endpoints/person';
import { openSnackbar } from 'store/application';
import {
  getAttributesPayload,
  getPersonAttribute,
  getPersonDateOfBirth,
  getPersonMedicalRecords,
  medicalFormValues,
  requiredFormValues,
} from './utils';
import SkeletonLoader from './skeletonLoader';
import { isOnboardedUser } from 'routes/route.utils';
import {
  hasEmptyProperties,
  hasNonEmptyProperty,
} from 'pages/ContactInformation/utils';
import { useStudyArm } from 'hooks/useStudyArm';

const initialFormValues = {
  monthOfBirth : '',
  dayOfBirth   : '',
  yearOfBirth  : '',
  ...requiredFormValues,
};
const pkdDiagnosisData = Object.values(PKDDiagnosis);
const relationshipData = Object.values(Relationship);
const kidneyTransplantData = ['Yes', 'No'];

export const MedicalInformation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Queries
  const {
    auth0User,
    isPersonLoading,
    isPersonAttributesFetching,
    personId,
    person,
  } = useGetPersonAndAttributes();
  const {
    personStudyArm,
    handleUpdatePersonStudyArmTable,
  } = useStudyArm(personId);

  const { personStudyRoles, isPersonStudyRoleLoading } =
    useGetPersonStudyRolesQuery(
      { personId },
      {
        skip             : !personId,
        selectFromResult : ({ data, isFetching, isLoading }) => ({
          personStudyRoles : data?.data?.map((attribute) => ({
            ...attribute.attributes,
            id : attribute.id,
          })),
          isPersonStudyRoleLoading : isLoading,
        }),
      }
    );

  // Mutations
  const [upsertPersonAttribute, { isLoading: isPersonAttributeUpserting }] =
    useUpsertPersonAttributeMutation();
  const [createPersonStudyRole, { isLoading: isPersonStudyRoleUpdating }] =
    useCreatePersonStudyRoleMutation();

  const personMedicalRecords = getPersonMedicalRecords(
    person?.attributes,
    medicalFormValues
  );
  const personDateOfBirth = getPersonDateOfBirth(person?.attributes);
  const defaultMedicalRecords = {
    ...initialFormValues,
    ...personDateOfBirth,
    ...personMedicalRecords,
  };

  const {
    formState: { isValid, defaultValues: formDefaultValues },
    control,
    reset,
    setValue,
    getValues,
    handleSubmit,
    trigger,
  } = useForm({
    defaultValues : defaultMedicalRecords,
  });

  const {
    monthOfBirth,
    yearOfBirth,
    dayOfBirth,
    pkdDiagnosis,
    patientRelationship,
    kidneyTransplant,
  } = getValues();
  const arrayOfYears = getArrayOfYears();

  // Memo
  const userIsBelowAge = useMemo(
    () =>
      Boolean(yearOfBirth) &&
      Boolean(monthOfBirth) &&
      Boolean(dayOfBirth) &&
      isBelowAge(`${yearOfBirth}-${monthOfBirth}-${dayOfBirth}`, 18),
    [yearOfBirth, monthOfBirth, dayOfBirth]
  );

  const filteredRelationshipData = useMemo(() => {
    return relationshipData
      .filter((relationship) =>
        userIsBelowAge
          ? relationship === Relationship.Parent
          : relationship !== Relationship.Parent
      )
      .map((relationship) => ({
        label : relationship,
        value : relationship,
      }));
  }, [userIsBelowAge]);

  // Handlers
  const onSubmit = handleSubmit(async (data) => {
    const parseData = {
      ...data,
      hasMigratedMedicalInfo        : 'Yes',
      communicationNewsletter       : 'true',
      communicationStudyOpportunity : 'true',
      communicationReminders        : 'true',
      date_of_birth                 : `${data.yearOfBirth}/${data.monthOfBirth}/${data.dayOfBirth.padStart(2, '0')}`,
      first_name                    : auth0User?.given_name,
      last_name                     : auth0User?.family_name,
    };
    delete parseData.yearOfBirth;
    delete parseData.monthOfBirth;
    delete parseData.dayOfBirth;
    const attributes = getPersonAttribute(
      person?.attributes,
      medicalFormValues
    );
    const body = getAttributesPayload(parseData, attributes);
    if (body.length > 0) {
      // upsert attributes
      const { error } = await upsertPersonAttribute({
        personId,
        body,
      });

      if (error) {
        return dispatch(
          openSnackbar({
            type : 'error',
            message :
              'Could not save your medical information. Please try again',
          })
        );
      }
    }

    // create person study role if non exists
    const userIsBelow18 =
      parseData?.date_of_birth && isBelowAge(parseData?.date_of_birth, 18);
    const studyRoleIds = [
      ...[
        StudyRoleId.Participant,
        ...(userIsBelow18 ? [StudyRoleId.Caregiver] : []),
      ],
    ];

    const studyRolesToAdd = studyRoleIds
      .filter(
        (id) =>
          !personStudyRoles
            .map((personStudyRole) => personStudyRole.studyRoleId)
            .includes(id)
      )
      .map((id) => ({
        personId,
        studyRoleId : id,
      }));

    let countError = 0;
    for (let i = 0; i < studyRolesToAdd.length; i++) {
      const { error } = await createPersonStudyRole({
        attribute : studyRolesToAdd[i],
      });
      if (error) {
        countError++;
      }
    }
    if (countError > 0) {
      return dispatch(
        openSnackbar({
          type    : 'error',
          message : 'Could not save your medical information. Please try again',
        })
      );
    }

    dispatch(
      openSnackbar({
        type    : 'success',
        message : 'Medical information successfully saved',
      })
    );
    dispatch(updateSecondaryOverviewStep('contact-information'));
    navigate('/contact-information', { replace: true });
  });

  // Effects
  // Unauthorized navigation
  useEffect(() => {
    if (isOnboardedUser(person?.state)) {
      return navigate('/homepage', { replace: true });
    }
  }, [person?.state]);

  const skeletonLoading = isPersonStudyRoleLoading || isPersonLoading;
  const hasNonEmptyProp = hasNonEmptyProperty(formDefaultValues);
  const hasEmptyProps = hasEmptyProperties(formDefaultValues);

  useEffect(() => {
    if (!skeletonLoading && hasEmptyProps) {
      reset(defaultMedicalRecords);
    }
  }, [skeletonLoading]);

  useEffect(() => {
    if (hasNonEmptyProp && !hasEmptyProps) {
      trigger();
    }
  }, [hasNonEmptyProp, hasEmptyProps]);

  useEffect(() => {
    const findPatientRelationship = filteredRelationshipData.find(
      (data) => data.value === personMedicalRecords.patientRelationship
    );
    setValue('patientRelationship', findPatientRelationship?.value ?? '');
    trigger();
  }, [userIsBelowAge]);

  useEffect(() => {
    handleUpdatePersonStudyArmTable(PersonStatus.Authenticated);
  }, [personStudyArm?.attributes?.studyArmStatusId]);

  return (
    <PKDOnboardingLayout>
      <FormWrapper
        image={MedicalInfoImage}>
        <Box>
          <Box>
            <FormTitle>Medical information</FormTitle>
            <Box
              maxWidth='613px'>
              <FormDescription>
                Telling us this information now helps us make sure we are asking
                you for the most relevant information later.
              </FormDescription>
            </Box>
            {skeletonLoading
              ? (
                <SkeletonLoader />
              )
              : (
                <>
                  {userIsBelowAge && (
                    <Alert
                      severity='error'>
                      {`If you’re under 18 years old, you’ll need a parent/guardian to 
                    register you and participate in the consent process`}
                    </Alert>
                  )}
                  <Box
                    marginTop='32px'>
                    <Typography>Patient date of birth</Typography>
                  </Box>
                  <FormRow
                    marginTop='8px' gridGap='16px' cols='3'>
                    <Box>
                      <Controller
                        control={control}
                        name='monthOfBirth'
                        rules={{
                          required : {
                            value   : true,
                            message : 'This field is required',
                          },
                        }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <ErrorWrapper>
                            <PKDSelect
                              id={`${getRandomIndex()}`}
                              label='MM'
                              value={value}
                              onChange={(e) => {
                                onChange(e);
                                trigger();
                              }}
                              error={error?.message && Boolean(monthOfBirth)}
                              hasErrors={
                                error &&
                              Boolean(monthOfBirth) && {
                                  type    : 'custom',
                                  message : error.message,
                                }
                              }
                              options={Array.from({ length: 12 }).map(
                                (_, index) => ({
                                  label : `${index + 1}`.padStart(2, '0'),
                                  value : `${index + 1}`.padStart(2, '0'),
                                })
                              )}
                            />
                          </ErrorWrapper>
                        )}
                      />
                    </Box>
                    <Box>
                      <Controller
                        control={control}
                        name='dayOfBirth'
                        rules={{
                          required : {
                            value   : true,
                            message : 'This field is required',
                          },
                        }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <ErrorWrapper>
                            <PKDSelect
                              id={`${getRandomIndex()}`}
                              onChange={(e) => {
                                onChange(e);
                                trigger();
                              }}
                              value={value}
                              error={error?.message && Boolean(dayOfBirth)}
                              hasErrors={
                                error &&
                              Boolean(dayOfBirth) && {
                                  type    : 'custom',
                                  message : error.message,
                                }
                              }
                              label='DD'
                              options={getDaysOfMonth(monthOfBirth, yearOfBirth)}
                            />
                          </ErrorWrapper>
                        )}
                      />
                    </Box>
                    <Box>
                      <Controller
                        control={control}
                        name='yearOfBirth'
                        rules={{
                          required : {
                            value   : true,
                            message : 'This field is required',
                          },
                          maxLength : {
                            value   : 4,
                            message : 'Invalid year',
                          },
                          minLength : {
                            value   : 4,
                            message : 'Invalid year',
                          },
                        }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <ErrorWrapper>
                            <PKDSelect
                              id={`${getRandomIndex()}`}
                              onChange={(e) => {
                                onChange(e);
                                trigger();
                              }}
                              error={error?.message && Boolean(yearOfBirth)}
                              hasErrors={
                                error &&
                              Boolean(yearOfBirth) && {
                                  type    : 'custom',
                                  message : error.message,
                                }
                              }
                              value={value}
                              label='YYYY'
                              options={arrayOfYears.map((year) => ({
                                label : year,
                                value : `${year}`,
                              }))}
                            />
                          </ErrorWrapper>
                        )}
                      />
                    </Box>
                  </FormRow>
                  <FormRow
                    marginTop='32px' gridGap='16px' cols='2'>
                    <Box>
                      <Controller
                        control={control}
                        name='pkdDiagnosis'
                        rules={{
                          required : {
                            value   : true,
                            message : 'This field is required',
                          },
                          validate : (value) => {
                            return (
                              pkdDiagnosisData.includes(value) ||
                            'Invalid PKD Diagnosis'
                            );
                          },
                        }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <ErrorWrapper>
                            <PKDSelect
                              id={`${getRandomIndex()}`}
                              onChange={(e) => {
                                onChange(e);
                                trigger();
                              }}
                              error={error?.message && Boolean(pkdDiagnosis)}
                              hasErrors={
                                error &&
                              Boolean(pkdDiagnosis) && {
                                  type    : 'custom',
                                  message : error.message,
                                }
                              }
                              value={value}
                              label='PKD Diagnosis'
                              options={pkdDiagnosisData.map((diagnosis) => ({
                                label : diagnosis,
                                value : diagnosis,
                              }))}
                            />
                          </ErrorWrapper>
                        )}
                      />
                    </Box>
                    <Box>
                      <Controller
                        control={control}
                        name='patientRelationship'
                        rules={{
                          required : {
                            value   : true,
                            message : 'This field is required',
                          },
                          validate : (value) => {
                            const findRelationship =
                            filteredRelationshipData.find(
                              (data) => data.value === value
                            );
                            return (
                              findRelationship || 'Invalid Patient Relationship'
                            );
                          },
                        }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <ErrorWrapper>
                            <PKDSelect
                              id={`${getRandomIndex()}`}
                              onChange={(e) => {
                                onChange(e);
                                trigger();
                              }}
                              error={
                                error?.message && Boolean(patientRelationship)
                              }
                              hasErrors={
                                error &&
                              Boolean(patientRelationship) && {
                                  type    : 'custom',
                                  message : error.message,
                                }
                              }
                              value={value}
                              label='Your relationship with patient'
                              options={filteredRelationshipData}
                            />
                          </ErrorWrapper>
                        )}
                      />
                    </Box>
                    <Box>
                      <Controller
                        control={control}
                        name='kidneyTransplant'
                        rules={{
                          required : {
                            value   : true,
                            message : 'This field is required',
                          },
                          validate : (value) => {
                            return (
                              kidneyTransplantData.includes(value) ||
                            'Invalid Kidney Transplant'
                            );
                          },
                        }}
                        render={({
                          field: { value, onChange },
                          fieldState: { error },
                        }) => (
                          <ErrorWrapper>
                            <PKDSelect
                              id={`${getRandomIndex()}`}
                              onChange={(e) => {
                                onChange(e);
                                trigger();
                              }}
                              value={value}
                              label='Kidney Transplant?'
                              error={error?.message && Boolean(kidneyTransplant)}
                              hasErrors={
                                error &&
                              Boolean(kidneyTransplant) && {
                                  type    : 'custom',
                                  message : error.message,
                                }
                              }
                              options={kidneyTransplantData.map((option) => ({
                                label : option,
                                value : option,
                              }))}
                            />
                          </ErrorWrapper>
                        )}
                      />
                    </Box>
                  </FormRow>
                  <Box
                    marginTop='42px' display='block'>
                    <Box
                      mb={5}>
                      <PKDFilledButton
                        onClick={onSubmit}
                        disabled={
                          isPersonAttributeUpserting ||
                        isPersonStudyRoleUpdating ||
                        isPersonAttributesFetching ||
                        !isValid
                        }
                        isLoading={
                          isPersonAttributeUpserting ||
                        isPersonStudyRoleUpdating ||
                        isPersonAttributesFetching
                        }>
                      Continue
                      </PKDFilledButton>
                    </Box>
                  </Box>
                </>
              )}
          </Box>
        </Box>
      </FormWrapper>
    </PKDOnboardingLayout>
  );
};
